import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Briefcase, Headphones, Code, Palette, Target, Crown, ArrowUpRight } from 'lucide-react';
import { services } from '../mock';

const iconMap = { Briefcase, Headphones, Code, Palette, Target, Crown };

// Bento spans - asymmetric layout
const spanMap = {
  0: 'md:col-span-2',
  1: 'md:col-span-1',
  2: 'md:col-span-1',
  3: 'md:col-span-1',
  4: 'md:col-span-1',
  5: 'md:col-span-2',
};

export default function Services() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="services" className="py-28 md:py-40 bg-[#050A08] relative overflow-hidden" data-testid="services-section">
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-emerald-500/[0.07] rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-20"
        >
          <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
            / What We Do
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.95] mb-6">
            Full-service support,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-400">handled brilliantly.</span>
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            From day-to-day admin to high-level strategy, our elite VAs plug into your business and deliver excellence across every function.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                whileHover={{ y: -6 }}
                className={`group relative ${spanMap[index]} bg-[#0D1612] rounded-3xl p-8 md:p-10 border border-white/[0.06] hover:border-emerald-500/40 transition-colors duration-500 overflow-hidden`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                <div className="relative z-10 flex items-start justify-between mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500 transition-colors duration-500">
                    <Icon className="w-7 h-7 text-emerald-400 group-hover:text-[#04120C] transition-colors duration-500" />
                  </div>
                  <span className="font-display text-5xl font-black text-white/[0.06] group-hover:text-emerald-500/20 transition-colors duration-500">
                    {service.number}
                  </span>
                </div>

                <div className="relative z-10">
                  <h3 className="font-display text-2xl font-bold text-white mb-3 flex items-center gap-2">
                    {service.title}
                    <ArrowUpRight className="w-5 h-5 text-emerald-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </h3>
                  <p className="text-slate-400 leading-relaxed mb-6">{service.description}</p>

                  <div className="flex flex-wrap gap-2">
                    {service.features.map((f, i) => (
                      <span key={i} className="text-xs text-emerald-200/70 bg-emerald-500/[0.07] border border-emerald-500/10 rounded-full px-3 py-1.5">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
